import { useEffect, useMemo, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import zhCnLocale from '@fullcalendar/core/locales/zh-cn';
import zhTwLocale from '@fullcalendar/core/locales/zh-tw';
import enLocale from '@fullcalendar/core/locales/en-gb';
import jaLocale from '@fullcalendar/core/locales/ja';

import { t, useTranslation } from '../i18n';

import styles from './style.module.less';
import type { ReactElement } from 'react';
import type { CalendarProps, EventContentArg } from './types';
import type { Locale } from '../i18n/types';
import type { LocaleInput } from '@fullcalendar/core';

/**
 * 语言映射：系统语言 -> FullCalendar locale
 */
const LOCALE_MAP: Record<Locale, LocaleInput> = {
  zh_cn: zhCnLocale,
  zh_hk: zhTwLocale,
  en: enLocale,
  jp: jaLocale
};


/**
 * 默认事件内容渲染
 * 显示类型图标和标题
 */
function defaultEventContent(arg: EventContentArg): { html: string } {
  const typeName = arg.event.extendedProps.typeName ?? '';
  const { title } = arg.event;

  const html = `
    <div class="zf-calendar-event">
      <div class="zf-calendar-event-type">${typeName}</div>
      <span class="zf-calendar-event-title" title="${title}">${title}</span>
    </div>
  `;

  return { html };
}

/**
 * 日历组件
 * 基于 FullCalendar 封装的日历组件
 */
export function FcCalendar({
  events,
  height = 600,
  maxWidth = 900,
  initialView = 'dayGridMonth',
  dayMaxEvents = 2,
  eventContent,
  className = '',
  style
}: CalendarProps): ReactElement {
  const calendarRef = useRef<FullCalendar>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // 获取当前语言
  const { i18n } = useTranslation();
  const currentLocale = LOCALE_MAP[i18n.language as Locale] ?? zhCnLocale;

  // 监听容器尺寸变化，自动更新日历尺寸
  useEffect(() => {
    const container = containerRef.current;
    if (container == null) {
      return;
    }

    const observer = new ResizeObserver(() => {
      calendarRef.current?.getApi().updateSize();
    });

    observer.observe(container);

    return (): void => {
      observer.disconnect();
    };
  }, []);

  // 合并样式
  const containerStyle = useMemo(() => ({
    maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
    margin: '0 auto',
    ...style
  }), [maxWidth, style]);

  // 事件内容渲染器
  const renderEventContent = eventContent ?? defaultEventContent;

  // 更多链接文本
  const moreLinkContent = (args: { num: number }): string => t('更多 ({{num}})', { num: args.num });

  return (
    <div ref={containerRef} className={`${styles.calendar} ${className}`} style={containerStyle}>
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin]}
        initialView={initialView}
        locale={currentLocale}
        buttonText={{ today: t('今天') }}
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: ''
        }}
        events={events}
        eventOrder='order'
        eventOrderStrict
        height={height}
        dayMaxEvents={dayMaxEvents}
        moreLinkContent={moreLinkContent}
        eventContent={renderEventContent}
      />
    </div>
  );
}
